import { type Observable } from 'rxjs'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type {
  GetCurrentUserFn,
  PrimeWorkspaceFn,
  PrimeUserFn,
} from '@/features/iam'
import type { Id } from '@/infra/id'
import type {
  SnackbarRetryWrapperFactory,
  SnackbarErrorHandlerFactory,
} from '@/features/shell'
import type { ConversationViewModel } from './ConversationViewModel'
import type { User } from '@/features/iam/users/User'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import type { PlaySoundFn } from '@/features/sound'

/**
 * Function that returns an {@link Observable} that emits the online status of a user.
 */
export type OnlineStatusForUserFn = (userId: Id) => Observable<boolean>

/**
 * Factory function for creating a conversation view model.
 *
 * @param getConversationId Reactive getter for the conversation ID
 */
export type ConversationViewModelFactory = (
  getConversationId: () => Id,
) => ConversationViewModel

/**
 * Reactive delegate for getting a stream of unread counts
 * for a conversation.
 */
export type UnreadCountFn = (conversationId: Id) => Observable<number>

/**
 * Options and dependencies needed by the messages feature.
 */
export interface MessagesFeatureOptions {
  token$: Observable<string>
  getCurrentUser: GetCurrentUserFn
  currentUser$: Observable<User>
  primeWorkspace: PrimeWorkspaceFn
  primeUser: PrimeUserFn
  createSnackbarRetryWrapper: SnackbarRetryWrapperFactory
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
  pageActivity$: Observable<PageActivityDescriptor>
  userActive$: Observable<boolean>
  playSound: PlaySoundFn
}

/**
 * Features relating to messages.
 */
export type MessagesFeature = ReturnType<typeof MessagesFeature>

export function MessagesFeature(options: MessagesFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalMessagesFeature(options),
  )

  const unreadCount: UnreadCountFn = (id) =>
    internal.defer((m) => m.conversationStore.unreadCount(id))

  const onlineStatusForUser: OnlineStatusForUserFn = (userId) =>
    internal.defer((mod) => mod.conversationStore.onlineStatusForUser(userId))

  return {
    // Internal
    internal,

    // Public
    unreadCount: unreadCount,
    onlineStatusForUser,

    incomingMessages$: internal.defer(
      (mod) => mod.conversationStore.incomingMessages$,
    ),

    createConversationViewModel: internal.lazy(
      (mod) => mod.createConversationViewModel,
    ),
  }
}
